import React, { useState } from 'react';
import { Platform, Pressable, View } from 'react-native';
import { Image } from 'expo-image';
import { useTheme } from '@/context/ThemeContext';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import { formatFileSize, getFileTypeCategory, getFileIconByExtension } from '@/constants/utils/files';

export const FILE_CATEGORIES = [
  { id: 'all', label: 'All files', icon: 'files' },
  { id: 'documents', label: 'Documents', icon: 'file-lines', color: '#6995F4' },
  { id: 'images', label: 'Images', icon: 'image', color: '#DD7DB6' },
  { id: 'audio', label: 'Audio', icon: 'music', color: '#A78BFA' },
  { id: 'video', label: 'Video', icon: 'film', color: '#F2AA6B' },
  { id: 'code', label: 'Code', icon: 'code', color: '#61BEA2' },
  { id: 'other', label: 'Other', icon: 'file', color: '#94A3B8' },
];

function FileMenu({ file, onOpen, onDetails, onDownload, onChat, onCollect, colors }) {
  const actions = [
    { id: 'open', title: 'Open file', icon: 'arrow-up-right-from-square', iosIcon: 'arrow.up.right.square', onSelect: () => onOpen(file) },
    { id: 'details', title: 'Details & rename', icon: 'pencil', iosIcon: 'pencil', onSelect: () => onDetails(file) },
    { id: 'download', title: 'Download', icon: 'arrow-down-to-line', iosIcon: 'arrow.down.to.line', onSelect: () => onDownload(file) },
    { id: 'chat', title: 'Chat with file', icon: 'message-dots', iosIcon: 'bubble.left', onSelect: () => onChat(file) },
    { id: 'collection', title: 'Add to collection', icon: 'folder-plus', iosIcon: 'folder.badge.plus', onSelect: () => onCollect(file) },
  ];
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild={Platform.OS === 'web'}>
        <Pressable accessibilityRole="button" accessibilityLabel={`Actions for ${file.name}`}
          style={{ width: 36, height: 36, borderRadius: 9, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.solid_color_block }}>
          <Icon name="ellipsis" size={16} color={colors.text_color_secondary} />
        </Pressable>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end" sideOffset={6} className={Platform.OS === 'web' ? 'DropdownMenuContent' : undefined}>
        {actions.map(action => Platform.OS === 'web'
          ? <DropdownMenuItem key={action.id} {...action} />
          : <DropdownMenu.Item key={action.id} onSelect={action.onSelect}>
            <DropdownMenu.ItemIcon ios={{ name: action.iosIcon }} />
            <DropdownMenu.ItemTitle>{action.title}</DropdownMenu.ItemTitle>
          </DropdownMenu.Item>)}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

export default React.memo(function LibraryFileItem({ file, width, list, compact, selected, selectionDisabled, onSelect, ...actions }) {
  const { colors } = useTheme();
  const [hovered, setHovered] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const category = getFileTypeCategory(file);
  const type = FILE_CATEGORIES.find(item => item.id === category) || FILE_CATEGORIES[6];
  const extension = String(file.extension || file.name?.split('.').pop() || 'file').replace(/^\./, '');
  const thumb = category === 'images' && !imageFailed ? (file.thumbnailPath || file.cdnPath) : null;
  const status = file.processingStatus?.status;
  const date = new Date(file.createdAt);
  const dateLabel = Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  const checkbox = (
    <Pressable accessibilityRole="checkbox" accessibilityLabel={`Select ${file.name}`}
      accessibilityState={{ checked: selected, disabled: selectionDisabled }} disabled={selectionDisabled}
      aria-checked={selected}
      onPress={() => onSelect(file)}
      style={{ width: 36, height: 36, alignItems: 'center', justifyContent: 'center', borderRadius: 9, backgroundColor: colors.solid_color_block }}>
      <View style={{ width: 18, height: 18, borderRadius: 5, borderWidth: 1.5, borderColor: selected ? '#477AF3' : colors.text_color_tertiary, backgroundColor: selected ? '#477AF3' : 'transparent', alignItems: 'center', justifyContent: 'center', opacity: selectionDisabled ? 0.35 : 1 }}>
        {selected && <Icon name="check" size={10} color="#fff" />}
      </View>
    </Pressable>
  );
  const preview = (
    <View style={{ height: list ? 44 : compact ? 120 : 148, width: list ? 44 : '100%', flexShrink: 0, borderRadius: list ? 9 : 0, backgroundColor: `${type.color}12`, alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
      {thumb ? <Image source={{ uri: thumb }} contentFit={list ? 'cover' : 'contain'} style={{ width: '100%', height: '100%' }} onError={() => setImageFailed(true)} accessibilityLabel={file.name} />
        : <View style={{ padding: list ? 0 : 18, borderRadius: 16, backgroundColor: list ? 'transparent' : `${type.color}16` }}>
          <Icon name={getFileIconByExtension(extension)} size={list ? 21 : 36} color={type.color} />
        </View>}
    </View>
  );
  const metadata = (
    <Text size="xs" color="secondary" numberOfLines={1}>
      {String(extension).toUpperCase()} · {formatFileSize(file.sizeInBytes)}
    </Text>
  );
  const processing = status && !['completed', 'skipped'].includes(status)
    ? <Text size="xxs" style={{ color: status === 'failed' ? colors.color_destructive : colors.text_color_secondary }}>{status === 'failed' ? 'Processing failed' : 'Preparing for chat…'}</Text> : null;

  if (list) return (
    <View style={{ flexDirection: 'row', alignItems: 'center', padding: compact ? 8 : 12, gap: 8, borderBottomWidth: 1, borderColor: colors.border_color_secondary, backgroundColor: selected ? `${'#477AF3'}10` : colors.solid_color_block }}>
      {checkbox}
      <Pressable accessibilityRole="button" accessibilityLabel={`Open ${file.name}`} onPress={() => actions.onOpen(file)} style={{ flex: 1, minWidth: 0, flexDirection: 'row', alignItems: 'center', gap: 12 }}>
        {preview}
        <View style={{ flex: 1, minWidth: 0, gap: 4 }}>
          <Text size="s" weight="medium" numberOfLines={1}>{file.name}</Text>
          {metadata}{processing}
        </View>
        {!compact && <Text size="xs" color="secondary" style={{ width: 100, textAlign: 'right' }}>{dateLabel}</Text>}
      </Pressable>
      <FileMenu file={file} colors={colors} {...actions} />
    </View>
  );

  return (
    <View style={{ width, borderRadius: 14, borderWidth: 1, borderColor: selected ? '#477AF3' : hovered ? colors.text_color_tertiary : colors.border_color_secondary, backgroundColor: colors.solid_color_block, overflow: 'hidden', ...(Platform.OS === 'web' && { transition: 'border-color 150ms ease' }) }}>
      <Pressable accessibilityRole="button" accessibilityLabel={`Open ${file.name}`} onPress={() => actions.onOpen(file)} onHoverIn={() => setHovered(true)} onHoverOut={() => setHovered(false)}>
        {preview}
        <View style={{ padding: 14, gap: 7 }}>
          <Text size="s" weight="medium" numberOfLines={2} style={{ height: 38, lineHeight: 19 }}>{file.name}</Text>
          {metadata}
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
            <Text size="xxs" color="tertiary">{dateLabel}</Text>
            <Text size="xxs" color="tertiary">{file.source === 'inference' ? 'Generated' : 'Uploaded'}</Text>
          </View>
          {processing}
        </View>
      </Pressable>
      <View style={{ position: 'absolute', top: 8, left: 8 }}>{checkbox}</View>
      <View style={{ position: 'absolute', top: 8, right: 8 }}><FileMenu file={file} colors={colors} {...actions} /></View>
    </View>
  );
});
